import * as React from "react";

import { cn } from "./cn";
import { FOCUS_RING } from "./theme";

export type AdvisorButtonVariant = "primary" | "secondary" | "ghost";
export type AdvisorButtonSize = "sm" | "md" | "icon";

const VARIANT_STYLES: Record<AdvisorButtonVariant, string> = {
  primary:
    "rounded-full border border-revampBlue bg-revampBlue text-white hover:bg-revampDarkBlue",
  secondary:
    "rounded-full border border-gray-200 bg-white text-gray-800 hover:bg-gray-100",
  // A step backwards or a de-emphasised aside: no surface of its own.
  ghost:
    "rounded-md border border-transparent bg-transparent text-gray-500 hover:text-gray-800",
};

const SIZE_STYLES: Record<AdvisorButtonSize, string> = {
  sm: "gap-1 px-2.5 py-1 text-xs",
  md: "gap-1.5 px-4 py-2 text-sm",
  // No padding utilities, so nothing competes with the fixed box in Tailwind's source order.
  // The only size that centres its content — a chip whose label wraps stays flush left.
  icon: "h-8 w-8 justify-center",
};

interface AdvisorButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: AdvisorButtonVariant;
  size?: AdvisorButtonSize;
}

/**
 * Every clickable control in the panel. The app-wide @components/Button is an antd + .less
 * component from a different visual language, so the advisor keeps its own — but only one.
 *
 * forwardRef because antd's Tooltip/Popconfirm clone their trigger and need a ref to position
 * against.
 */
export const AdvisorButton = React.forwardRef<
  HTMLButtonElement,
  AdvisorButtonProps
>(({ variant = "secondary", size = "md", className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    {...props}
    className={cn(
      "inline-flex items-center font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40",
      VARIANT_STYLES[variant],
      SIZE_STYLES[size],
      FOCUS_RING,
      className
    )}
  />
));

AdvisorButton.displayName = "AdvisorButton";
